import { createEffect, onCleanup, onMount } from 'solid-js';
import { gameStore } from '../../store/gameStore';
import { visualTier } from '../../utils/tiers';
import { computeMultiplierScale, LERP_BASE, LERP_DIFF_FACTOR, LERP_MAX, LERP_SNAP_THRESHOLD } from '../../game/constants';

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export default function MultiplierView() {
  const { phase, multiplier } = gameStore;
  let rafId: number | null = null;
  let lastTs = 0;
  let displayed = 1.0;
  let target = 1.0;
  let prevText = '';
  let frozen = false;
  let frozenVal = 1.0;
  let lastLive = 1.0;
  let displayEl: HTMLElement | null = null;
  let valueEl: HTMLElement | null = null;
  let game: any;

  const frame = (ts: number) => {
    if (ts - lastTs < 0) lastTs = 0;

    const m = multiplier();
    target = isFinite(m) ? m : 1.0;

    if (!frozen && isFinite(target)) lastLive = target;

    if (frozen) {
      target = frozenVal;
      displayed = frozenVal;
    } else {
      const diff = Math.abs(target - displayed);
      if (diff > LERP_SNAP_THRESHOLD) {
        displayed = lerp(displayed, target, Math.min(LERP_BASE + diff * LERP_DIFF_FACTOR, LERP_MAX));
      } else {
        displayed = target;
      }
    }

    const txt = displayed.toFixed(2);

    if (displayEl && valueEl && txt !== prevText) {
      valueEl.textContent = txt;
      prevText = txt;
      displayEl.setAttribute('data-tier', visualTier(target));
      displayEl.style.setProperty('--mult-base-scale', computeMultiplierScale(target).toFixed(3));
      displayEl.classList.remove('pre-init-hidden');
    }

    lastTs = ts;
    rafId = requestAnimationFrame(frame);
  };

  const freeze = (val: number) => {
    frozen = true;
    frozenVal = val;
    displayed = val;
    target = val;
    prevText = '';
  };

  onMount(() => {
    displayEl = document.getElementById('multiplier-display');
    valueEl = document.getElementById('multiplier-value');

    try { game = (window as any).memePumpApp?.getGame?.(); } catch {}

    try {
      game?.on?.('crash', (e: any) => {
        const v = Number(e?.data?.multiplier);
        if (isFinite(v) && v > 0) freeze(v);
      });
    } catch {}

    if (rafId == null) rafId = requestAnimationFrame(frame);
  });

  createEffect(() => {
    const p = phase();
    if (p === 'CRASH' && !frozen) {
      const mNow = multiplier();
      freeze((isFinite(mNow) && mNow > 0) ? mNow : lastLive);
    }
  });

  createEffect(() => {
    const p = phase();
    if (!displayEl) return;
    displayEl.classList.toggle('is-in-round', !!p && p !== 'BET_TIME' && p !== 'ROUND_START');
  });

  createEffect(() => {
    const p = phase();
    if (displayEl && valueEl && (p === 'BET_TIME' || p === 'ROUND_START')) {
      frozen = false;
      frozenVal = 1.0;
      lastLive = 1.0;
      prevText = '';
      displayed = 1.0;
      target = 1.0;
      valueEl.textContent = '1.00';
      displayEl.setAttribute('data-tier', 't1');
      displayEl.style.setProperty('--mult-base-scale', computeMultiplierScale(1).toFixed(3));
      displayEl.classList.remove('crashed', 'crash-final', 'crash-move-init', 'updating');
      displayEl.style.removeProperty('--crash-final-scale');
    }
  });

  onCleanup(() => {
    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = null;
  });

  return null as any;
}
